import { NextComponentType, NextPageContext } from 'next';
import { Public } from '../../common/services/Public.type';
import { PreloadJob } from './PreloadJob';

export type NextComponentTypeWithPreloader = NextComponentType & {
  preloader?: (job: Public<PreloadJob>) => Promise<void>;
};

/** Decorates a Page component with a preloading routine, internally using `getInitialProps()`.
 * @param Component The page-level component which needs preloading
 * @param performCustomPreloading An async function to be run as a preloading routine. Doesn't return any data, but receives a `PreloadJob` which can be used to get and store data to be made accessible during render.
 * Even if no custom preloading callback is provided, some data will be preloaded — see `preloadDefaults()`.
 * This differs from NextJS's standard method of just passing in props, because the preloaded data
 * is made available to the whole tree via React Context.
 */
export const withPreloader = (
  Component: NextComponentTypeWithPreloader,
  performCustomPreloading: (job: Public<PreloadJob>) => Promise<void>,
) => {
  Component.preloader = performCustomPreloading;

  Component.getInitialProps = async (context: NextPageContext): Promise<StandardPageProps> => {
    // eslint-disable-next-line no-restricted-globals
    const job = new PreloadJob(context);
    const { flags, logger } = job.services;

    // Allow disabling the preloader altogether with a URL flag, for debugging
    if (flags.noPreload) return {};

    if (performCustomPreloading) {
      await logger.profile('Preloading', () => performCustomPreloading(job));
    }

    return { placeholder: null };
  };

  return Component;
};

export interface StandardPageProps {
  /**
   * Nextjs issues a warning if there are no visible properties on the props returned.
   * Since we know we are populating the preloader anyway, we supply a placeholder prop.
   */
  placeholder?: null;
}
